/**
 * T8015 · Connectivity orchestrator — wire signals → hysteresis → store.
 *
 * SPEC ANCHOR: `specs/009-data-fetching-audit/05-new-infra-sdk-changes.html`
 * §L1353-L1556.
 *
 * Combines:
 *   - NATS heartbeat signal (`installNatsHeartbeat`).
 *   - REST online/offline signal (`installOnlineOfflineHandler`).
 *   - 3 s hysteresis filter (`createHysteresisFilter`).
 *   - `deriveState({restHealthy, natsHealthy})` → 4-state FSM.
 *
 * On every emitted transition:
 *   1. `connectivityStore.applyTransition(t)` — writes to the singleton store.
 *   2. `emitToBus(t)` — broadcasts the transition for telemetry / panels.
 *   3. `fanOutRealtimeRefetch(qc, t)` — touches every realtime query so
 *      the next refetch path flushes through `setOptions` (T8004 bug fix).
 */

import type { QueryClient } from '@tanstack/react-query';
import { connectivityStore } from '../../core/connectivity-store.js';
import { createHysteresisFilter } from '../fsm/hysteresis.js';
import { type ConnTransition, deriveState } from '../fsm/state-machine.js';
import { fanOutRealtimeRefetch } from '../reconnect/fan-out.js';
import { subscribeConnectHealth } from '../signals/connect-health.js';

export interface OrchestratorDeps {
  readonly qc: QueryClient;
  readonly installNatsHeartbeat: (h: {
    readonly setNatsHealthy: (ok: boolean, cause: string) => void;
    readonly recordTick: () => void;
    readonly onReconnect: () => void;
  }) => () => void;
  readonly installOnlineOfflineHandler: (h: {
    readonly setRestHealthy: (ok: boolean, cause: string) => void;
  }) => () => void;
  readonly emitToBus: (t: ConnTransition) => void;
  /** Reconcile non-generated realtime owners after transport recovery. */
  readonly onNatsReconnect?: () => void;
  /** Hysteresis window. Defaults to 3 000 ms per spec L1442. */
  readonly windowMs?: number;
  /** Injected clock — defaults to `performance.now`. */
  readonly now?: () => number;
}

export function installConnectivityOrchestrator(deps: OrchestratorDeps): () => void {
  // `natsHealthy` is the AND of two independent sources: the heartbeat (only
  // live once a CLIENT exists) and the connect-bootstrap signal (covers the
  // failed/hung INITIAL connect, before any client/heartbeat). Either going
  // false marks NATS unhealthy; both must be true to be CONNECTED.
  const health = { restHealthy: true, heartbeatNats: true, connectNats: true };

  const filter = createHysteresisFilter({
    ...(deps.windowMs !== undefined ? { windowMs: deps.windowMs } : {}),
    ...(deps.now !== undefined ? { now: deps.now } : {}),
    // Seed from the live store, NOT a hardcoded CONNECTED. The orchestrator is
    // re-installed whenever the NATS client identity changes — including a
    // reconnect that rebuilds a fresh socket while the FSM sits in
    // DEGRADED_NO_NATS. Hardcoding CONNECTED there meant the fresh orchestrator
    // believed it was already connected, so an all-healthy recompute observed
    // CONNECTED == settled and emitted NOTHING — leaving the store (and the
    // "Live data" pill) stuck on "reconnecting" until a page refresh. Seeding
    // from the store lets the recovery emit the real DEGRADED→CONNECTED edge.
    initial: connectivityStore.getState().state,
    emit: (t) => {
      connectivityStore.getState().applyTransition(t);
      deps.emitToBus(t);
      void fanOutRealtimeRefetch(deps.qc, t).catch((): void => {});
    },
  });

  function recompute(cause: string): void {
    filter.observe(
      deriveState({
        restHealthy: health.restHealthy,
        natsHealthy: health.heartbeatNats && health.connectNats,
      }),
      cause,
    );
  }

  const stopNats = deps.installNatsHeartbeat({
    setNatsHealthy: (h, c) => {
      health.heartbeatNats = h;
      recompute(c);
    },
    recordTick: (): void => {},
    onReconnect: (): void => deps.onNatsReconnect?.(),
  });

  const stopRest = deps.installOnlineOfflineHandler({
    setRestHealthy: (h, c) => {
      health.restHealthy = h;
      recompute(c);
    },
  });

  // Connect-bootstrap health — the connect path reports failure/recovery here
  // so a never-established (rejected/hung) initial connect surfaces as
  // DEGRADED_NO_NATS instead of staying optimistically CONNECTED.
  const stopConnect = subscribeConnectHealth((h, c) => {
    health.connectNats = h;
    recompute(c);
  });

  return (): void => {
    stopNats();
    stopRest();
    stopConnect();
    // Drop any in-flight settle so a stale transition can't land in the store
    // after this orchestrator is replaced (reconnect / HMR / client swap).
    filter.cancel();
  };
}
